import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, onSnapshot, deleteField } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './useAuth';

const DEFAULT_VALUES = {
  weeklyData: { weeks: {} },
  alerts: [
    { id: 1, message: 'Remember to check your daily points', active: true },
    { id: 2, message: 'Weekly target reminder', active: true }
  ],
  projectSettings: {},
  receivedAirdrops: [],
  calendarEvents: [],
  customAirdrops: [],
  subscription: 'free'
};

export const useStorageData = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [weeklyData, setWeeklyData] = useState(DEFAULT_VALUES.weeklyData);
  const [alerts, setAlerts] = useState(DEFAULT_VALUES.alerts);
  const [projectSettings, setProjectSettings] = useState(DEFAULT_VALUES.projectSettings);
  const [receivedAirdrops, setReceivedAirdrops] = useState(DEFAULT_VALUES.receivedAirdrops);
  const [calendarEvents, setCalendarEvents] = useState(DEFAULT_VALUES.calendarEvents);
  const [customAirdrops, setCustomAirdrops] = useState(DEFAULT_VALUES.customAirdrops);
  const [subscription, setSubscription] = useState(DEFAULT_VALUES.subscription);

  const getUserDocRef = () => {
    if (!user) return null;
    return doc(db, 'users', user.uid);
  };

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const docRef = getUserDocRef();
    if (!docRef) return;

    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setWeeklyData(data.weeklyData || DEFAULT_VALUES.weeklyData);
        setAlerts(data.alerts || DEFAULT_VALUES.alerts);
        setProjectSettings(data.projectSettings || DEFAULT_VALUES.projectSettings);
        setReceivedAirdrops(data.receivedAirdrops || DEFAULT_VALUES.receivedAirdrops);
        setCalendarEvents(data.calendarEvents || DEFAULT_VALUES.calendarEvents);
        setCustomAirdrops(data.customAirdrops || DEFAULT_VALUES.customAirdrops);
        setSubscription(data.subscription || DEFAULT_VALUES.subscription);
      } else {
        setWeeklyData(DEFAULT_VALUES.weeklyData);
        setAlerts(DEFAULT_VALUES.alerts);
        setProjectSettings(DEFAULT_VALUES.projectSettings);
        setReceivedAirdrops(DEFAULT_VALUES.receivedAirdrops);
        setCalendarEvents(DEFAULT_VALUES.calendarEvents);
        setCustomAirdrops(DEFAULT_VALUES.customAirdrops);
        setSubscription(DEFAULT_VALUES.subscription);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error loading data from Firestore:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const saveToFirestore = async (newData) => {
    if (!user) return;
    
    const docRef = getUserDocRef();
    if (!docRef) return;

    try {
      await setDoc(docRef, {
        ...newData,
        updatedAt: new Date().toISOString(),
        userEmail: user.email
      }, { merge: true });
    } catch (error) {
      console.error('Error saving to Firestore:', error);
    }
  };

  // Special save function that overwrites the entire field (for deletions)
  const saveFieldToFirestore = async (fieldName, newData) => {
    if (!user) return;
    
    const docRef = getUserDocRef();
    if (!docRef) return;

    try {
      // Get current document
      const docSnap = await getDoc(docRef);
      const currentData = docSnap.exists() ? docSnap.data() : {};
      
      // Overwrite the specific field entirely
      await setDoc(docRef, {
        ...currentData,
        [fieldName]: newData,
        updatedAt: new Date().toISOString(),
        userEmail: user.email
      });
    } catch (error) {
      console.error('Error saving to Firestore:', error);
    }
  };

  const updateWeeklyData = (newData) => {
    setWeeklyData(newData);
    saveFieldToFirestore('weeklyData', newData);
  };

  const updateAlerts = (newData) => {
    setAlerts(newData);
    saveToFirestore({ alerts: newData });
  };

  const updateProjectSettings = (newData) => {
    setProjectSettings(newData);
    // Use saveFieldToFirestore to properly handle deletions
    saveFieldToFirestore('projectSettings', newData);
  };

  const updateReceivedAirdrops = (newData) => {
    setReceivedAirdrops(newData);
    saveToFirestore({ receivedAirdrops: newData });
  };

  const updateCalendarEvents = (newData) => {
    setCalendarEvents(newData);
    saveToFirestore({ calendarEvents: newData });
  };

  const updateCustomAirdrops = (newData) => {
    setCustomAirdrops(newData);
    saveToFirestore({ customAirdrops: newData });
  };

  const isPro = subscription === 'pro';

  return {
    loading,
    weeklyData,
    setWeeklyData: updateWeeklyData,
    alerts,
    setAlerts: updateAlerts,
    projectSettings,
    setProjectSettings: updateProjectSettings,
    receivedAirdrops,
    setReceivedAirdrops: updateReceivedAirdrops,
    calendarEvents,
    setCalendarEvents: updateCalendarEvents,
    customAirdrops,
    setCustomAirdrops: updateCustomAirdrops,
    subscription,
    isPro
  };
};
